import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getDocs,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { findEventByCodeOrSlug } from "../utils/eventLookup";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) {
    value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  }
  return value >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const textBytes = (value) => new TextEncoder().encode(value);

const writeUint16 = (value) => new Uint8Array([value & 0xff, (value >> 8) & 0xff]);

const writeUint32 = (value) =>
  new Uint8Array([
    value & 0xff,
    (value >> 8) & 0xff,
    (value >> 16) & 0xff,
    (value >> 24) & 0xff,
  ]);

const makeZip = (files) => {
  const chunks = [];
  const centralDirectory = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = textBytes(file.name);
    const data = file.data;
    const checksum = crc32(data);
    const localHeader = [
      writeUint32(0x04034b50),
      writeUint16(20),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(checksum),
      writeUint32(data.length),
      writeUint32(data.length),
      writeUint16(nameBytes.length),
      writeUint16(0),
      nameBytes,
    ];

    chunks.push(...localHeader, data);

    centralDirectory.push({
      nameBytes,
      checksum,
      size: data.length,
      offset,
    });

    offset += localHeader.reduce((sum, chunk) => sum + chunk.length, 0) + data.length;
  });

  const centralStart = offset;
  centralDirectory.forEach((file) => {
    const header = [
      writeUint32(0x02014b50),
      writeUint16(20),
      writeUint16(20),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(file.checksum),
      writeUint32(file.size),
      writeUint32(file.size),
      writeUint16(file.nameBytes.length),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint16(0),
      writeUint32(0),
      writeUint32(file.offset),
      file.nameBytes,
    ];
    chunks.push(...header);
    offset += header.reduce((sum, chunk) => sum + chunk.length, 0);
  });

  const centralSize = offset - centralStart;
  chunks.push(
    writeUint32(0x06054b50),
    writeUint16(0),
    writeUint16(0),
    writeUint16(centralDirectory.length),
    writeUint16(centralDirectory.length),
    writeUint32(centralSize),
    writeUint32(centralStart),
    writeUint16(0)
  );

  return new Blob(chunks, { type: "application/zip" });
};

const csvEscape = (value = "") => `"${String(value).replace(/"/g, '""')}"`;

const buildKeepsakeHtml = ({ event, entries, textOnlyMessages }) => {
  const title = escapeHtml(event?.title || "Event");

  const photoCardsHtml = entries
    .map((entry, index) => {
      return `
        <figure
          class="photo-card"
          style="transform: rotate(${index % 2 === 0 ? -1.5 : 1.5}deg);"
        >
          <img src="${escapeHtml(entry.url)}" alt="Guest memory" />
        </figure>
      `;
    })
    .join("");

  const noteCards = [
    ...entries
      .filter((entry) => entry.message)
      .map((entry) => ({
        message: entry.message,
        name: entry.name,
      })),
    ...textOnlyMessages.map((msg) => ({
      message: msg.message,
      name: msg.name || "Anonymous",
    })),
  ];

  const notesHtml = noteCards
    .map((note, index) => {
      return `
        <article
          class="message-note"
          style="transform: rotate(${index % 2 === 0 ? -1 : 1}deg);"
        >
          <p>"${escapeHtml(note.message || "")}"</p>
          <span>- ${escapeHtml(note.name || "Anonymous")}</span>
        </article>
      `;
    })
    .join("");

  const hasContent = entries.length > 0 || noteCards.length > 0;

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title} Keepsake</title>
        <style>
          @page {
            size: A4;
            margin: 14mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            background: #f8eaf2;
            color: #27212b;
            font-family: Arial, Helvetica, sans-serif;
          }

          .keepsake {
            min-height: 100vh;
            padding: 22px;
            background:
              radial-gradient(circle at 15% 10%, rgba(244, 114, 182, 0.2), transparent 24%),
              radial-gradient(circle at 85% 0%, rgba(168, 85, 247, 0.18), transparent 28%),
              linear-gradient(135deg, #fff7fb 0%, #f5edff 100%);
          }

          .header {
            margin: 0 auto 18px;
            max-width: 900px;
            text-align: center;
          }

          .eyebrow {
            margin: 0 0 6px;
            color: #9d174d;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }

          h1 {
            margin: 0;
            color: #831843;
            font-size: 30px;
            line-height: 1.15;
          }

          .subtitle {
            margin: 10px auto 0;
            max-width: 620px;
            color: #67576b;
            font-size: 14px;
          }

          .poster {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
            gap: 18px;
            max-width: 960px;
            margin: 0 auto;
            padding: 8px 0;
          }

          .photo-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin: 0;
            padding: 6px 6px 14px;
            border: 1px solid #f5d5e1;
            border-radius: 6px;
            background: rgba(255, 255, 255, 0.94);
            box-shadow: 0 14px 30px rgba(124, 45, 92, 0.18);
          }

          .photo-card img {
            display: block;
            width: 100%;
            aspect-ratio: 4 / 5;
            min-height: 96px;
            border-radius: 4px;
            border: 1px solid #f1d6e2;
            object-fit: cover;
          }

          .message-note {
            break-inside: avoid;
            page-break-inside: avoid;
            min-height: 120px;
            padding: 14px 16px;
            border: 1px solid rgba(249, 168, 212, 0.75);
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.9);
            box-shadow: 0 10px 24px rgba(124, 45, 92, 0.16);
          }

          .message-note p {
            margin: 0;
            color: #3f3344;
            font-size: 13px;
            font-style: italic;
            line-height: 1.38;
          }

          .message-note span {
            display: block;
            margin-top: 8px;
            color: #806b83;
            font-size: 11px;
            text-align: right;
          }

          .empty {
            margin: 48px auto;
            max-width: 520px;
            border-radius: 8px;
            background: white;
            padding: 24px;
            color: #67576b;
            text-align: center;
          }

          @media print {
            body {
              background: white;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }

            .keepsake {
              min-height: auto;
              padding: 0;
            }

            .photo-card,
            .message-note {
              box-shadow: none;
            }
          }
        </style>
      </head>
      <body>
        <main class="keepsake">
          <header class="header">
            <p class="eyebrow">FestiGram keepsake</p>
            <h1>${title}'s Memory Card</h1>
            <p class="subtitle">Photos and messages shared by guests.</p>
          </header>
          ${
            hasContent
              ? `
                ${
                  photoCardsHtml
                    ? `<section class="poster">${photoCardsHtml}</section>`
                    : ""
                }
                ${
                  notesHtml
                    ? `<section class="poster">${notesHtml}</section>`
                    : ""
                }
              `
              : `<div class="empty">No messages or photos have been added yet.</div>`
          }
        </main>
        <script>
          const images = Array.from(document.images);
          const ready = Promise.all(
            images.map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });
            })
          );

          ready.then(() => {
            setTimeout(() => {
              window.focus();
              window.print();
            }, 350);
          });
        </script>
      </body>
    </html>
  `;
};

const EventCollagePage = () => {
  const { shortCode } = useParams();
  const [event, setEvent] = useState(null);
  const [entries, setEntries] = useState([]);
  const [textOnlyMessages, setTextOnlyMessages] = useState([]);
  const [userId, setUserId] = useState(null);
  const [eventId, setEventId] = useState(null);

  const reactionsMap = {
    likes: "Like",
    hearts: "Love",
    laughs: "Laugh",
    party: "Party",
  };
  const [reactionsData, setReactionsData] = useState({});

  useEffect(() => {
    if (!userId || !eventId) return;

    const unsubscribeFns = [];

    entries.forEach((entry) => {
      const fileName = entry.fileName;
      const refPath = doc(
        db,
        "users",
        userId,
        "events",
        eventId,
        "reactions",
        fileName
      );

      const unsub = onSnapshot(refPath, (docSnap) => {
        if (docSnap.exists()) {
          setReactionsData((prev) => ({
            ...prev,
            [fileName]: docSnap.data(),
          }));
        }
      });

      unsubscribeFns.push(unsub);
    });

    return () => unsubscribeFns.forEach((unsub) => unsub());
  }, [entries, userId, eventId]);

  const handleReact = async (fileName, type) => {
    const refPath = doc(
      db,
      "users",
      userId,
      "events",
      eventId,
      "reactions",
      fileName
    );
    const snap = await getDoc(refPath);
    if (snap.exists()) {
      await updateDoc(refPath, {
        [type]: (snap.data()[type] || 0) + 1,
      });
    } else {
      await setDoc(refPath, {
        likes: 0,
        hearts: 0,
        laughs: 0,
        party: 0,
        [type]: 1,
      });
    }
  };

  const handleDownloadKeepsake = () => {
    const popup = window.open("", "_blank");

    if (!popup) {
      alert("Please allow pop-ups to download the keepsake PDF.");
      return;
    }

    popup.document.open();
    popup.document.write(
      buildKeepsakeHtml({ event, entries, textOnlyMessages })
    );
    popup.document.close();
  };

  const handleDownloadZip = async () => {
    const files = [];
    const csvRows = [
      ["Name", "Message", "Image File"].map(csvEscape).join(","),
      ...[
        ...entries.map((entry) => ({
          name: entry.name,
          message: entry.message,
          imageName: entry.fileName,
        })),
        ...textOnlyMessages.map((message) => ({
          name: message.name || "Anonymous",
          message: message.message,
          imageName: "",
        })),
      ].map((row) =>
        [row.name, row.message, row.imageName].map(csvEscape).join(",")
      ),
    ];

    files.push({
      name: "messages.csv",
      data: textBytes(csvRows.join("\n")),
    });

    for (const entry of entries) {
      try {
        const response = await fetch(entry.url);
        const buffer = await response.arrayBuffer();
        files.push({
          name: `photos/${entry.fileName || "photo.jpg"}`,
          data: new Uint8Array(buffer),
        });
      } catch (err) {
        console.warn("Could not add photo to zip", entry.fileName, err);
      }
    }

    const zip = makeZip(files);
    const url = URL.createObjectURL(zip);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(event?.title || "event")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")}-memories.zip`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchData = async () => {
      const result = await findEventByCodeOrSlug(shortCode);

      if (result) {
        const userIdFromPath = result.userId;
        const eventIdFromPath = result.eventId;

        setEvent(result.event);
        setUserId(userIdFromPath);
        setEventId(eventIdFromPath);

        const msgSnap = await getDocs(
          collection(
            db,
            "users",
            userIdFromPath,
            "events",
            eventIdFromPath,
            "messages"
          )
        );
        const messages = msgSnap.docs
          .map((messageDoc) => messageDoc.data())
          .filter((message) => message.moderationStatus !== "needsReview");

        const matched = messages
          .filter((message) => message.imageUrl)
          .map((message) => ({
            url: message.imageUrl,
            fileName: message.imageName || message.imageUrl,
            name: message.name || "Anonymous",
            message: message.message || "",
          }));

        const unmatched = messages.filter((message) => !message.imageUrl);

        setEntries(matched);
        setTextOnlyMessages(unmatched);
      }
    };

    fetchData();
  }, [shortCode]);

  const hasKeepsakeContent =
    entries.length > 0 || textOnlyMessages.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 p-6">
      <div className="mx-auto mb-6 flex max-w-5xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-pink-500">
            Memory collage
          </p>
          <h1 className="text-3xl font-bold text-pink-700 drop-shadow sm:text-4xl">
            {event?.title || "Event"} Memory Collage
          </h1>
        </div>
        <button
          type="button"
          onClick={handleDownloadKeepsake}
          disabled={!hasKeepsakeContent}
          className="rounded-md bg-pink-600 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-pink-700 disabled:cursor-not-allowed disabled:bg-pink-300"
        >
          Download Keepsake PDF
        </button>
        <button
          type="button"
          onClick={handleDownloadZip}
          disabled={!hasKeepsakeContent}
          className="rounded-md bg-white px-5 py-3 text-sm font-bold text-pink-600 shadow-md ring-1 ring-pink-200 transition hover:bg-pink-50 disabled:cursor-not-allowed disabled:text-pink-300"
        >
          Download All ZIP
        </button>
      </div>

      {entries.length > 0 && (
        <div className="mb-12 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {entries.map((entry, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center overflow-hidden rounded-lg bg-white p-2 shadow-md"
            >
              <img
                src={entry.url}
                alt={`Upload ${idx + 1}`}
                className="h-64 w-full rounded-md border object-cover"
              />

              {entry.message && (
                <p className="mt-2 px-2 text-center text-sm italic text-gray-700">
                  "{entry.message}"
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">- {entry.name}</p>

              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {Object.entries(reactionsMap).map(([type, label]) => (
                  <button
                    key={type}
                    onClick={() => handleReact(entry.fileName, type)}
                    className="rounded-full bg-pink-50 px-3 py-1 text-xs font-semibold text-pink-700 transition hover:scale-105 hover:bg-pink-100"
                  >
                    {label}
                    <span className="ml-1">
                      {reactionsData[entry.fileName]?.[type] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {textOnlyMessages.length > 0 && (
        <div className="mx-auto max-w-5xl rounded-lg bg-white p-4 shadow">
          <h2 className="mb-4 text-center text-xl font-semibold text-pink-600">
            Messages
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {textOnlyMessages.map((msg, i) => (
              <div
                key={i}
                className="rounded-md border-l-4 border-pink-300 bg-pink-50 p-4 shadow-sm"
              >
                <p className="mb-3 text-sm italic leading-relaxed text-gray-700">
                  "{msg.message}"
                </p>
                <p className="text-right text-xs text-gray-500">
                  - {msg.name || "Anonymous"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasKeepsakeContent && (
        <div className="mx-auto max-w-xl rounded-lg bg-white p-6 text-center text-gray-600 shadow">
          No messages or photos have been added yet.
        </div>
      )}
    </div>
  );
};

export default EventCollagePage;
